import { SYSTEMS } from '../../constants/systems';
import {
  calculateTotalEnergyConsumption,
  extractApplicationAbr,
  extractSwtSize,
} from '../../helpers/helpers';

// Image file names per program (the state key differs from the file name for
// heatingSchedule and optionalConstantTemp).
const IMAGES = Object.freeze({
  instantHeat: 'logo-instantHeat',
  snowSensor: 'logo-snowSensor',
  optionalConstantTemp: 'logo-constantTemp',
  heatingSchedule: 'logo-schedule',
  windFactor: 'logo-windFactor',
  fanOnly: 'tgs-fanOnly',
});
const DISABLED_IMAGES = Object.freeze({ ...IMAGES, fanOnly: 'logo-fanOnly' });

// The order the mobile program icon looks through. Instant heat never shows
// as "ready" there.
const ICON_ORDER = [
  'instantHeat',
  'snowSensor',
  'heatingSchedule',
  'windFactor',
  'optionalConstantTemp',
  'fanOnly',
];
const READY_ORDER = ['snowSensor', 'windFactor', 'heatingSchedule', 'optionalConstantTemp'];

const HATS = Object.freeze({
  off: [
    'MC-machine-header1-off',
    'MC-machine-header-mediumSize-off',
    'MC-machine-header-largeSize-off',
  ],
  faults: [
    'MC-machine-header1-faults',
    'MC-machine-header-mediumSize-faults',
    'MC-machine-header-largeSize-faults',
  ],
  normal: [
    'MC-machine-header1',
    'MC-machine-header-medium-size',
    'MC-machine-header-long-size',
  ],
});

/** The card's title: `name #size app.` on desktop, `name #size-system` on mobile. */
export const cardTitle = ({ machineName, heatingSystem }, system, isMobile) => {
  const size = extractSwtSize(heatingSystem);
  return isMobile
    ? `${machineName} #${size}-${system}`
    : `${machineName} #${size} ${extractApplicationAbr(heatingSystem)}.`;
};

/** 0, 1 or 2: the header grows at 28 and 46 title characters. */
export const titleSize = (length) => (length < 28 ? 0 : length < 46 ? 1 : 2);

/** The desktop header's title image, sized to the title and coloured by state. */
export const hatImage = (title, { isOff, isFaults }) => {
  const state = isOff ? 'off' : isFaults ? 'faults' : 'normal';
  return `/images/${HATS[state][titleSize(title.length)]}.svg`;
};

/** A program's image, or its greyed version. */
export const programImage = (program, disabled = false) =>
  disabled
    ? `/images/${DISABLED_IMAGES[program]}-disabled.svg`
    : `/images/${IMAGES[program]}.svg`;

// TGS stores the fan as a boolean; every other program as { isActivated, isReady }.
export const isRunning = (device, program) =>
  program === 'fanOnly' ? Boolean(device.fanOnly) : Boolean(device[program]?.isActivated);

export const isProgramReady = (device, program) =>
  program !== 'fanOnly' && Boolean(device[program]?.isReady);

/**
 * The mobile card's single program icon: a running program, else a ready
 * one, else the one picked on the mobile program row. `programs` limits it
 * to what the system offers.
 */
export const programIcon = (device, programs) => {
  const offered = (order) => order.filter((program) => programs.includes(program));
  const running = offered(ICON_ORDER).find((program) => isRunning(device, program));
  const ready = offered(READY_ORDER).find((program) => isProgramReady(device, program));
  const picked = offered(ICON_ORDER).find((program) => device.mobileSelectedProgram?.[program]);
  const shown = running ?? ready ?? picked;
  return {
    src: shown ? programImage(shown) : null,
    isActivated: Boolean(running),
    isReady: Boolean(ready),
  };
};

/** Total consumption: energy in kw, or TGS gas in M³ (FT³ for °F operators). */
export const consumptionOf = ({ reading }, system, isF) => {
  const { isGas } = SYSTEMS[system];
  return {
    value: calculateTotalEnergyConsumption(reading, system, isF),
    unit: isGas ? (isF ? 'FT³' : 'M³') : 'kw',
    source: isGas ? 'gas' : 'energy',
  };
};
